import './Dialog.scss';
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';

export default function Dialog({
  className = '',
  actions,
  closeOnOverlay = true,
  describedBy,
  open,
  onClose,
  title,
  children,
  onSubmit,
  submitLabel = 'Envoyer',
  cancelLabel = 'Annuler',
  loading = false,
  size = 'medium',
  portal = false,
}) {
  const generatedId = useId();
  const titleId = `custom-dialog-title-${generatedId}`;
  const panelRef = useRef(null);
  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;
    const focusableSelector = [
      'button:not(:disabled)',
      '[href]',
      'input:not(:disabled)',
      'select:not(:disabled)',
      'textarea:not(:disabled)',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const focusFirstElement = () => {
      const firstFocusable = panelRef.current?.querySelector(focusableSelector);
      if (firstFocusable) {
        firstFocusable.focus({ preventScroll: true });
        return;
      }
      panelRef.current?.focus({ preventScroll: true });
    };

    const handleKeyDown = (event) => {
      // Only the foreground dialog handles keys when a detail opens a dialog.
      const dialogs = [...document.querySelectorAll('[role="dialog"]')];
      if (dialogs[dialogs.length - 1] !== panelRef.current) return;
      if (event.key === 'Escape') {
        onCloseRef.current?.();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) {
        return;
      }

      const focusableElements = [...panelRef.current.querySelectorAll(focusableSelector)]
        .filter((element) => element.offsetParent !== null);

      if (!focusableElements.length) {
        event.preventDefault();
        panelRef.current.focus({ preventScroll: true });
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    const focusTimer = window.setTimeout(focusFirstElement, 0);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus?.({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  const dialog = (
    <div
      className={`custom-dialog ${className}`.trim()}
      role="presentation"
      onMouseDown={closeOnOverlay ? onClose : undefined}
      onClick={portal ? event => event.stopPropagation() : undefined}
      onSubmit={portal ? event => event.stopPropagation() : undefined}
    >
      <div
        ref={panelRef}
        className={`custom-dialog__panel custom-dialog__panel--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="custom-dialog__header">
          <h2 className="custom-dialog__title" id={titleId}>
            {title}
          </h2>
          <IconButton
            className="custom-dialog__close"
            ariaLabel="Fermer"
            onClick={onClose}
            disabled={loading}
            size="small"
          >
            x
          </IconButton>
        </div>
        <div className="custom-dialog__content">
          {children}
        </div>
        {(actions || onSubmit) && (
          <div className="custom-dialog__actions">
            {actions || (
              <>
                <Button variant="tertiary" onClick={onClose} disabled={loading}>
                  {cancelLabel}
                </Button>
                <Button variant="primary" onClick={onSubmit} disabled={loading} loading={loading}>
                  {loading ? 'Chargement...' : submitLabel}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
  return portal ? createPortal(dialog, document.body) : dialog;
}
